import { FC, useEffect, useMemo, useState } from 'react'
import documentService, {
  CreditApplicationDocumentsGroup,
  DocumentGroupItem,
} from '../../../services/documentService'
import { DocumentRow } from './DocumentRow'
import { Select } from '../../ui/Select'
import { formatDateLong } from '../../../utils/date'

interface DocumentSectionProps {
  title: string
  documents: DocumentGroupItem[]
}

const DocumentSection: FC<DocumentSectionProps> = ({ title, documents }) => (
  <div className="bg-white border border-olr-grey-2 rounded-lg p-6">
    <h3 className="text-olr-primary font-bold mb-4">{title}</h3>
    {documents.length === 0 ? (
      <p className="text-sm text-olr-body">No hay documentos disponibles.</p>
    ) : (
      <div className="space-y-3">
        {documents.map(document => (
          <DocumentRow key={document.id} document={document} />
        ))}
      </div>
    )}
  </div>
)

const groupLabel = (group: CreditApplicationDocumentsGroup): string => {
  const { brand, subBrand, modelYear } = group.vehicle
  const fecha = formatDateLong(String(group.createdAt))
  return `${brand} ${subBrand} ${modelYear}${fecha ? ` — ${fecha}` : ''}`
}

export const MyDocumentsContent: FC = () => {
  const [groups, setGroups] = useState<CreditApplicationDocumentsGroup[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const result =
          await documentService.getMyCreditApplicationDocumentsByApplication()
        if (cancelled) return
        setGroups(result)
        setSelectedId(result[0]?.creditApplicationId ?? null)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()

    return () => {
      cancelled = true
    }
  }, [])

  const selectedGroup = useMemo(
    () => groups.find(group => group.creditApplicationId === selectedId),
    [groups, selectedId]
  )

  return (
    <>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Mis documentos</h2>

      {isLoading ? (
        <div className="bg-white border border-olr-grey-2 rounded-lg p-12 text-center text-olr-body">
          Cargando documentos...
        </div>
      ) : groups.length === 0 ? (
        <div className="bg-white border border-olr-grey-2 rounded-lg p-12 text-center text-olr-body">
          No hay documentos disponibles.
        </div>
      ) : (
        <div className="space-y-6">
          <div className="max-w-md">
            <Select
              label="Solicitud"
              value={selectedId ?? undefined}
              onValueChange={setSelectedId}
              options={groups.map(group => ({
                value: group.creditApplicationId,
                label: groupLabel(group),
              }))}
            />
          </div>

          <DocumentSection
            title="Básicos"
            documents={selectedGroup?.basicos ?? []}
          />
          <DocumentSection
            title="Estados de cuenta"
            documents={selectedGroup?.estadosDeCuenta ?? []}
          />
        </div>
      )}
    </>
  )
}

export default MyDocumentsContent
